import React, { useState, useEffect, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import styled from 'styled-components';
import { useSelector, useDispatch, useStore } from "react-redux";
import { RootState } from "../store/store";
import { updateUser } from "../reducers/authSlice";
import api from "../api/axiosInstance";
import NotificationModal from './NotificationModal';
import { Link } from 'react-router-dom';
import { confirmationOutcome, mirrorOutcome, outcomeOf } from './subscriptionOutcome';
import { httpStatus } from '../api/errors';

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    text-align: center;
`;

const LoadingIcon = styled(FaSpinner)`
    color: #007bff;
    font-size: 4rem;
    margin-bottom: 20px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const SuccessIcon = styled(FaCheckCircle)`
    color: green;
    font-size: 4rem;
    margin-bottom: 20px;
`;

const ErrorIcon = styled(FaTimesCircle)`
    color: red;
    font-size: 4rem;
    margin-bottom: 20px;
`;

const MessageText = styled.p`
    font-size: 1.5rem;
    margin: 0;
`;

const AccountLink = styled(Link)`
    margin-top: 20px;
    color: #f58802;
    font-size: 1.1rem;
`;

/**
 * What a member is told while Stripe has not finished. It promises no access and blames no card:
 * either would be a guess, and the webhook is what settles it a moment later.
 *
 * It claims nothing about the money either, which is why it no longer opens with "Platba prebehla" —
 * "the payment went through". This screen cannot know that. Everything that is neither a confirmed
 * grant nor a decline lands here, and that includes a confirmation that came back with an error and
 * no PaymentIntent at all: a stale or malformed client secret fails that way, and nothing was
 * charged. Stating that the payment went through would be false for that member and unverifiable for
 * the rest, while "we are still checking" is true of every case that reaches this line — including
 * the one where the charge really is settling.
 *
 * It also asks for nothing. This line used to end with "Ak sa nič nestane do minúty, obnovte
 * stránku." — and reloading re-runs the effect below, which re-POSTs the purchase. That is the one
 * action available on this screen that can charge a card a second time, so it is the one thing the
 * copy must never suggest. The member is pointed at their account page instead, where the membership
 * appears on its own.
 */
const PENDING_MESSAGE = 'Platbu ešte overujeme. Ak prebehla, prístup sa aktivuje o chvíľu.';

const PaymentStatus: React.FC = () => {
    const stripe = useStripe();
    const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
    const [message, setMessage] = useState<string>('Spracovávam platbu...');
    const user = useSelector((state: RootState) => state.auth.user);
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const accessToken = useSelector((state: RootState) => state.auth.accessToken);
    const [isModalOpen, setModalOpen] = useState<boolean>(false); // Modal visibility state
    // Where to go next, for the two outcomes that leave the member somewhere to be: a refused card is
    // replaced from the account page, and a payment still settling turns into a membership there
    // without anybody doing anything. A link rather than a retry button on purpose — see
    // PENDING_MESSAGE.
    const [showAccountLink, setShowAccountLink] = useState<boolean>(false);
    
    // Ref to prevent duplicate subscription creation
    const isProcessingRef = useRef(false);
    const processedIntentRef = useRef<string | null>(null);

    useEffect(() => {
        let current = true;
        const isCurrent = () => current && store.getState().auth.user?.id === user?.id
            && store.getState().auth.accessToken === accessToken;
        const recordVerifiedPayment = async (paymentIntentId: string | null | undefined) => {
            if (!paymentIntentId || !isCurrent()) return;
            try {
                await api.post(`/orders/create?userId=${user?.id}`, { paymentIntentId });
            } catch (orderError: any) {
                // Stripe has already confirmed the charge; order bookkeeping is retryable and
                // must not change either the payment outcome or the access mirror flow.
                if (isCurrent()) console.warn('Objednávku sa nepodarilo zaznamenať:', httpStatus(orderError));
            }
        };
        const checkPaymentStatus = async () => {
            if (!stripe || !user?.id || !accessToken) return;
            // A login change cancels the old effect. Do not run its return URL as another buyer.
            if (isProcessingRef.current) {
                setModalOpen(false);
                setStatus('pending');
                setMessage('Skontrolujte členstvo v aktuálne prihlásenom účte.');
                setShowAccountLink(true);
                return;
            }

            const urlParams = new URLSearchParams(window.location.search);
            
            // Check if this is a PaymentIntent return (subscription invoice or one-off pass)
            const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');
            const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
            // No productId in the return URL means "the plan on sale", and only the server knows
            // which row that is. The old `: 1` fallback guessed an id, and an id that is not the
            // recurring row buys a different membership than the one that was paid for.
            const productIdParam = urlParams.get('productId');
            const parsedProductId = productIdParam ? parseInt(productIdParam, 10) : NaN;
            const productId: number | null = isNaN(parsedProductId) ? null : parsedProductId;
            const productQuery = productId !== null ? `?productId=${productId}` : '';

            // Handle the actual payment; this path never creates another subscription.
            if (paymentIntentClientSecret) {
                if (processedIntentRef.current === paymentIntentClientSecret) return;

                // Claim before the first await so a re-render cannot start a second return flow.
                isProcessingRef.current = true;
                processedIntentRef.current = paymentIntentClientSecret;
                const { paymentIntent, error } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);
                if (!isCurrent()) return;

                if (error || !paymentIntent) {
                    setStatus('error');
                    setMessage('Nepodarilo sa načítať platbu.');
                    return;
                }

                switch (paymentIntent.status) {
                    case 'succeeded':
                        await recordVerifiedPayment(paymentIntent.id);
                        if (!isCurrent()) return;
                        try {
                            // The webhook grants access. This server-verified mirror refreshes the UI.
                            const updatedUser = await api.put(`/account/premium/${user?.id}${productQuery}`, {});
                            if (!isCurrent()) return;
                            dispatch(updateUser(updatedUser.data));
                            setStatus('success');
                            setMessage('Platba prebehla úspešne!');
                            setModalOpen(true);
                        } catch (err: any) {
                            if (!isCurrent()) return;
                            // 402 = the server could not yet match the payment in Stripe. The webhook still
                            // activates the membership, so treat it like 409 rather than alarming a
                            // customer whose card was charged. Read through the shared reader rather
                            // than reaching into the axios error twice.
                            const mirrorStatus = httpStatus(err);
                            if (mirrorStatus === 409 || mirrorStatus === 402) {
                                setStatus('success');
                                setMessage('Platba prebehla úspešne!');
                                setModalOpen(true);
                            } else {
                                // Stripe has confirmed the charge. Do not call it a payment failure
                                // or restart checkout because the access mirror is unavailable.
                                setStatus('pending');
                                setMessage('Platba prebehla. Aktiváciu členstva ešte overujeme.');
                                setShowAccountLink(true);
                            }
                        }
                        break;

                    case 'requires_payment_method':
                    case 'canceled':
                        setStatus('error');
                        setMessage('Platba sa nepodarila, skúste znova.');
                        setShowAccountLink(true);
                        break;

                    case 'processing':
                    case 'requires_capture':
                        setStatus('pending');
                        setMessage('Platba sa ešte spracováva. Skontrolujte svoje členstvo pred ďalším pokusom.');
                        setShowAccountLink(true);
                        break;

                    default:
                        setStatus('pending');
                        setMessage(PENDING_MESSAGE);
                        setShowAccountLink(true);
                        break;
                }
                return;
            }

            // Retain SetupIntent returns for older web bundles.
            if (setupIntentClientSecret) {
                if (processedIntentRef.current === setupIntentClientSecret) return;

                isProcessingRef.current = true;
                processedIntentRef.current = setupIntentClientSecret;
                const { setupIntent, error } = await stripe.retrieveSetupIntent(setupIntentClientSecret);
                if (!isCurrent()) return;

                if (error || !setupIntent) {
                    setStatus('error');
                    setMessage('Nepodarilo sa načítať SetupIntent.');
                    return;
                }

                switch (setupIntent.status) {
                    case 'succeeded':
                        try {
                            const pmId = setupIntent.payment_method;

                            // The productId is omitted entirely rather than sent as null: the
                            // server reads a missing one as "the single recurring product", and a
                            // null in the body would be a value it has to guess at.
                            const created = await api.post(`/subscription/${user?.id}`, productId !== null
                                ? { paymentMethodId: pmId, productId: productId }
                                : { paymentMethodId: pmId });

                            if (!isCurrent()) return;

                            // This answer used to be discarded — the POST was awaited and nothing
                            // was read off it — so a declined card and a charge waiting on a bank's
                            // 3-D Secure dialog both came out as "Platba prebehla úspešne!" over an
                            // account that had been granted nothing.
                            let outcome = outcomeOf(created.data ?? {});

                            if (outcome === 'needs_action') {
                                // The intent already carries the card the member has just saved, so
                                // there is nothing to send but the secret. Stripe.js opens the
                                // bank's dialog here and resolves once it closes.
                                const confirmed = await stripe.confirmCardPayment(created.data.clientSecret);
                                if (!isCurrent()) return;
                                if (confirmed.error) {
                                    // The code alone: error objects in this app are never logged whole.
                                    console.warn('3-D Secure sa nedokončilo:', confirmed.error.code);
                                }
                                // Stripe.js hangs the intent off its error object when it reports
                                // one, so both places are read before anything is decided. The
                                // ternary this replaces called everything that was not `succeeded` a
                                // decline — a charge still settling included — and sent the member
                                // off to try another card, which is how a second subscription gets
                                // created against the card the first one is still charging.
                                outcome = confirmationOutcome(
                                    confirmed.paymentIntent?.status ?? confirmed.error?.payment_intent?.status);
                            }

                            if (outcome === 'declined') {
                                setStatus('error');
                                setMessage('Platba bola zamietnutá. Skúste inú kartu.');
                                setShowAccountLink(true);
                                break;
                            }

                            if (outcome === 'pending') {
                                setStatus('pending');
                                setMessage(PENDING_MESSAGE);
                                setShowAccountLink(true);
                                break;
                            }

                            // The succeeded invoice is independently verifiable even if the
                            // membership mirror is temporarily unavailable.
                            await recordVerifiedPayment(created.data?.paymentIntentId);
                            if (!isCurrent()) return;

                            // Granted. What follows is a mirror of what Stripe already knows, so
                            // that a member who has just paid is not left looking at a free account
                            // until the webhook lands. Its "not yet" answers are not failures.
                            try {
                                const updatedUser = await api.put(`/account/premium/${user?.id}${productQuery}`, {});
                                if (!isCurrent()) return;
                                dispatch(updateUser(updatedUser.data));
                                setStatus('success');
                                setMessage('Platba prebehla úspešne!');
                                setModalOpen(true);
                            } catch (mirrorError: any) {
                                if (!isCurrent()) return;
                                // The charge is already settled by the time this runs, and the
                                // webhook — not this endpoint — is what grants the membership. So
                                // the mirror answering "I cannot see it yet" (402) is a member who
                                // has paid, and is told so. Only 503 leaves anything genuinely
                                // unknown. Telling a paid member to wait here was the other half of
                                // the refresh loop: nothing re-polls, nothing dispatches, and the
                                // only move left to them was the one that re-enters the purchase.
                                //
                                // What this does NOT do is dispatch a user. There is no account
                                // payload in a 402 to dispatch, so the Redux user stays non-premium
                                // until the next load — the same as before this release. The
                                // membership itself is not in doubt: the webhook writes it.
                                const mirror = mirrorOutcome(httpStatus(mirrorError));
                                if (mirror === 'granted') {
                                    setStatus('success');
                                    setMessage('Platba prebehla úspešne!');
                                    setModalOpen(true);
                                } else if (mirror === 'pending') {
                                    setStatus('pending');
                                    setMessage(PENDING_MESSAGE);
                                    setShowAccountLink(true);
                                } else {
                                    setStatus('error');
                                    setMessage('Chyba pri aktivácii prémia.');
                                    isProcessingRef.current = false;
                                    processedIntentRef.current = null;
                                }
                            }
                        } catch (err: any) {
                            if (!isCurrent()) return;
                            // The subscription could not be created at all: the request was refused
                            // or never arrived, and nothing was charged.
                            setStatus('error');
                            setMessage('Chyba pri vytváraní predplatného.');
                            isProcessingRef.current = false;
                            processedIntentRef.current = null;
                        }
                        break;

                    case 'requires_payment_method':
                        setStatus('error');
                        setMessage('Platba sa nepodarila, skúste znova.');
                        break;

                    default:
                        setStatus('pending');
                        setMessage(PENDING_MESSAGE);
                        setShowAccountLink(true);
                        break;
                }
                return;
            }

            // No valid client secret found
            setStatus('error');
            setMessage('Chýba informácia o platbe.');
        };

        checkPaymentStatus().catch(() => {
            if (!isCurrent()) return;
            setStatus('pending');
            setMessage(PENDING_MESSAGE);
            setShowAccountLink(true);
        });
        return () => { current = false; };
    }, [stripe, user?.id, accessToken, dispatch, store]);

    const handleSettingsUpdate = (data: any) => {
        // Handle any updates when settings are changed in the modal
    };

    const handleCloseModal = () => {
        setModalOpen(false); // Close the modal
    };

    return (
        <>
            <MessageContainer>
                {status === 'loading' && <LoadingIcon />}
                {status === 'success' && <SuccessIcon />}
                {status === 'error' && <ErrorIcon />}
                {/* `pending` shows no icon on purpose: a spinner would say something is still
                    running on this page, and nothing is — it finishes in the webhook. */}
                <MessageText>{message}</MessageText>
                {showAccountLink && <AccountLink to="/account">Späť na môj účet</AccountLink>}
            </MessageContainer>

            <NotificationModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                onSettingsUpdate={handleSettingsUpdate}
            />
        </>
    );
};

export default PaymentStatus;
